/**
 * Asset Link Types for Frontend
 *
 * TypeScript types for the asset trackable links and QR code system
 * frontend components and API integration.
 */

export interface AssetLink {
  id: string;
  assetId: string;
  tenantId: string;
  trackableUrl: string;
  qrTrackableUrl: string;
  shortCode: string;
  metadata: AssetLinkMetadata;
  qrCodes: QRCode[];
  customQRCodes: CustomQRCode[];
  clickCount: number;
  scanCount: number;
  lastClickedAt?: string;
  lastScannedAt?: string;
  status: LinkStatus;
  createdAt: string;
  updatedAt: string;
}

export interface QRCode {
  id: string;
  assetLinkId: string;
  svgContent: string;
  downloadUrl: string;
  size: QRCodeSize;
  format: QRCodeFormat;
  cacheKey: string;
  generatedAt: string;
}

export interface CustomQRCode {
  id: string;
  assetLinkId: string;
  filename: string;
  s3Key: string;
  downloadUrl: string;
  contentType: string;
  size: number;
  uploadedAt: string;
}

export interface AssetLinkMetadata {
  description?: string;
  tags?: string[];
  customParameters?: Record<string, any>;
}

// Enums
export type LinkStatus = "ACTIVE" | "INACTIVE" | "EXPIRED";
export type QRCodeSize = "SMALL" | "MEDIUM" | "LARGE";
export type QRCodeFormat = "SVG" | "PNG" | "PDF";

// Input Types
export interface GenerateAssetLinkInput {
  assetId: string;
  metadata?: AssetLinkMetadataInput;
  generateQRCode?: boolean;
  qrCodeOptions?: QRCodeOptionsInput;
}

export interface AssetLinkMetadataInput {
  description?: string;
  tags?: string[];
  customParameters?: Record<string, any>;
}

export interface QRCodeOptionsInput {
  size?: QRCodeSize;
  includeActivQMSLogo?: boolean;
  formats?: QRCodeFormat[];
}

export interface QRCodeCustomizationInput {
  foregroundColor?: string;
  backgroundColor?: string;
  logoSize?: number;
  errorCorrectionLevel?: string;
}

export interface UpdateAssetLinkInput {
  metadata?: AssetLinkMetadataInput;
  status?: LinkStatus;
}

export interface UploadCustomQRInput {
  assetLinkId: string;
  filename: string;
  contentType: string;
  fileSize: number;
}

export interface GenerateQRCodeInput {
  linkId: string;
  size?: number;
  format?: QRCodeFormat;
  logoUrl?: string;
  customization?: QRCodeCustomizationInput;
}

export interface AssetLinkFilterInput {
  assetId?: string;
  status?: LinkStatus;
  section?: string;
  level?: string;
  createdAfter?: string;
  createdBefore?: string;
  search?: string;
  hasQRCode?: boolean;
  hasCustomQR?: boolean;
}

// Response Types
export interface AssetLinkConnection {
  items: AssetLink[];
  nextToken?: string;
  total: number;
}

export interface QRCodeResult {
  qrCodeId: string;
  downloadUrl: string;
  format: QRCodeFormat;
  size: number;
  createdAt: string;
  expiresAt: string;
}

export interface CustomQRUploadResult {
  customQRCode: CustomQRCode;
  uploadUrl: string;
  uploadFields: Record<string, any>;
}

// Analytics Types
export interface AssetLinkAnalytics {
  totalClicks: number;
  uniqueClicks: number;
  clicksByDevice: DeviceClickStats;
  clicksByLocation: LocationClickStats[];
  clicksByHour: HourlyClickStats[];
  seatLevelAnalytics?: SeatLevelAnalytics;
  lastClickAt?: string;
}

export interface SeatLevelAnalytics {
  clicksBySeat: SeatClickStats[];
  clicksBySection: SectionClickStats[];
  heatMapData: HeatMapPoint[];
}

export interface SeatClickStats {
  seatNumber: string;
  section: string;
  level: string;
  clicks: number;
  lastClickAt?: string;
}

export interface SectionClickStats {
  section: string;
  level: string;
  totalSeats: number;
  clickedSeats: number;
  totalClicks: number;
  engagementRate: number;
}

export interface HeatMapPoint {
  x: number;
  y: number;
  intensity: number;
  seatNumber?: string;
  section?: string;
}

export interface DeviceClickStats {
  mobile: number;
  desktop: number;
  tablet: number;
  other: number;
}

export interface LocationClickStats {
  country: string;
  region?: string;
  city?: string;
  clicks: number;
}

export interface HourlyClickStats {
  hour: number;
  clicks: number;
  date: string;
}

// UI State Types
export interface AssetLinkUIState {
  selectedAssetLinks: Set<string>;
  showCreateModal: boolean;
  showAnalyticsModal: boolean;
  selectedAssetLink: AssetLink | null;
  filter: AssetLinkFilterInput;
  viewMode: "list" | "grid";
}

// Constants
export const QR_CODE_SIZE_LABELS: Record<QRCodeSize, string> = {
  SMALL: "Small (150px)",
  MEDIUM: "Medium (300px)",
  LARGE: "Large (600px)",
};

export const LINK_STATUS_LABELS: Record<LinkStatus, string> = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  EXPIRED: "Expired",
};
